import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Event as MapEvent, JamSesh, Listing, Post, Shop, Spot, User, Zip } from '../types';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import ExclamationIcon from './icons/ExclamationIcon';
import CameraIcon from './icons/CameraIcon';

interface ReportDoc {
  id: string;
  reporterId: string;
  timestamp?: any;
  type?: 'post' | 'zip' | 'comment' | 'spot' | 'sesh' | 'event' | 'listing' | 'general';
  targetId?: string;
  parentType?: 'posts' | 'zips' | 'spots';
  parentId?: string;
  reportedUserId?: string;
  description?: string;
}

interface CorrectionDoc {
  id: string;
  targetType: 'spot' | 'skatepark' | 'shop';
  targetId: string;
  targetName?: string;
  text: string;
  submittedBy?: { id: string; username?: string };
  createdAt?: any;
  reviewed?: boolean;
}

type Category = 'content' | 'comments' | 'map' | 'listings' | 'profiles' | 'shopSubmits' | 'parkSubmits' | 'corrections' | 'bannedUsers';

interface DeveloperDashboardProps {
  onClose: () => void;
  onDeletePost: (postId: string) => void;
  onDeleteZip: (zipId: string) => void;
  onDeleteComment: (parentCollection: 'posts' | 'zips' | 'spots', parentId: string, commentId: string) => void;
  onDeleteSpot: (spotId: string) => void;
  onDeleteJamSesh: (jamSeshId: string) => void;
  onDeleteEvent: (eventId: string) => void;
  onDeleteListing: (listingId: string) => void;
  onBanUser: (userId: string) => void;
  onUnbanUser: (userId: string) => void;
  shops: Shop[];
  parks: Spot[];
  onAcceptShopSubmission: (shopId: string) => void;
  onAcceptParkSubmission: (spotId: string) => void;
  allUsers: User[];
  // What reports point at, so each report can show the reporter, the reported post / Zip / pin / listing and who made it.
  posts?: Post[];
  zips?: Zip[];
  spots?: Spot[];
  jamSeshes?: JamSesh[];
  events?: MapEvent[];
  listings?: Listing[];
  // Opens a person's profile (closes the dashboard first).
  onViewUser?: (user: User) => void;
  // Totals across the app, shown in the Analytics card.
  contentCounts?: { posts: number; zips: number; spots: number; parks: number; shops: number; seshes: number; events: number; listings: number; threads: number };
}

const formatTimestamp = (ts: any): string => {
  try {
    const date = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : typeof ts === 'number' ? new Date(ts) : null;
    if (!date) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
};

const CATEGORY_LABELS: Record<Category, string> = {
  content: 'Reported Posts & Zips',
  comments: 'Reported Comments',
  map: 'Reported Spots/Sesh/Events',
  listings: 'Reported Listings',
  profiles: 'Reported Profiles',
  shopSubmits: 'Shop Submits',
  parkSubmits: 'Park Submits',
  corrections: 'Corrections',
  bannedUsers: 'Banned Users',
};

type PersonRef = { id?: string; username?: string; avatarUrl?: string } | null | undefined;

// A person as a tappable "@TAG" chip (avatar + name). Tapping opens their profile.
const PersonChip: React.FC<{ person: PersonRef; usersById: Map<string, User>; onOpen?: (user: User) => void; label?: string }> = ({ person, usersById, onOpen, label }) => {
  const full = person?.id ? usersById.get(person.id) : undefined;
  const username = full?.username || person?.username;
  const avatar = full?.avatarUrl || person?.avatarUrl;
  const name = username ? `@${String(username).toUpperCase()}` : person?.id ? `Unknown user (${person.id.slice(0, 6)}…)` : 'Unknown user';
  const openable = Boolean(onOpen && (full || (person?.id && username)));
  return (
    <button
      type="button"
      disabled={!openable}
      onClick={() => { if (openable) onOpen!((full || { id: person!.id, username, avatarUrl: avatar }) as User); }}
      className="inline-flex max-w-full items-center gap-2 rounded-full bg-white/10 py-1 pl-1 pr-3 text-left transition-colors enabled:hover:bg-white/20 disabled:opacity-70"
    >
      {avatar ? <img src={avatar} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" /> : <span className="h-6 w-6 shrink-0 rounded-full bg-white/20" />}
      <span className="truncate text-sm font-bold text-white">{label ? `${label} ` : ''}{name}</span>
    </button>
  );
};

const MediaThumb: React.FC<{ url?: string; isVideo?: boolean }> = ({ url, isVideo }) => {
  if (!url) return null;
  return isVideo
    ? <video src={`${url}#t=0.1`} className="h-20 w-20 shrink-0 rounded-lg bg-black object-cover" muted playsInline preload="metadata" />
    : <img src={url} alt="" className="h-20 w-20 shrink-0 rounded-lg bg-black object-cover" />;
};

// A reported comment lives under its post / Zip / spot, so it is fetched when the report is shown.
const ReportedComment: React.FC<{ report: ReportDoc; usersById: Map<string, User>; onOpen?: (user: User) => void }> = ({ report, usersById, onOpen }) => {
  const [state, setState] = useState<{ text?: string; user?: PersonRef; gone?: boolean } | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!report.parentType || !report.parentId || !report.targetId) { setState({ gone: true }); return; }
    getDoc(doc(db, report.parentType, report.parentId, 'comments', report.targetId))
      .then((snap) => { if (!cancelled) setState(snap.exists() ? { text: String(snap.data().text || ''), user: snap.data().user } : { gone: true }); })
      .catch(() => { if (!cancelled) setState({ gone: true }); });
    return () => { cancelled = true; };
  }, [report.parentType, report.parentId, report.targetId]);
  if (!state) return <p className="text-xs text-gray-500">Loading comment…</p>;
  if (state.gone) return <p className="text-sm text-gray-400">This comment no longer exists.</p>;
  return (
    <div className="space-y-1.5">
      <PersonChip person={state.user} usersById={usersById} onOpen={onOpen} label="by" />
      <p className="whitespace-pre-wrap break-words text-sm text-gray-200">{state.text}</p>
    </div>
  );
};

const DeveloperDashboard: React.FC<DeveloperDashboardProps> = ({
  onClose,
  onDeletePost,
  onDeleteZip,
  onDeleteComment,
  onDeleteSpot,
  onDeleteJamSesh,
  onDeleteEvent,
  onDeleteListing,
  onBanUser,
  onUnbanUser,
  shops,
  parks,
  onAcceptShopSubmission,
  onAcceptParkSubmission,
  allUsers,
  posts = [],
  zips = [],
  spots = [],
  jamSeshes = [],
  events = [],
  listings = [],
  onViewUser,
  contentCounts,
}) => {
  const [reports, setReports] = useState<ReportDoc[]>([]);
  const [corrections, setCorrections] = useState<CorrectionDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const snap = await getDocs(query(collection(db, 'reports'), orderBy('timestamp', 'desc')));
        setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ReportDoc)));
        try {
          const correctionSnap = await getDocs(query(collection(db, 'corrections'), orderBy('createdAt', 'desc')));
          setCorrections(correctionSnap.docs.map((d) => ({ id: d.id, ...d.data() } as CorrectionDoc)));
        } catch (err) {
          console.warn('Failed to load corrections (rules deployed?):', err);
        }
      } catch (err: any) {
        console.error('Failed to load reports:', err);
        setLoadError('Could not load reports. If this is the first time using this, make sure the updated Firestore rules have been deployed.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const markResolved = (id: string) => setResolvedIds((prev) => new Set(prev).add(id));

  const postsAndZips = reports.filter((r) => r.type === 'post' || r.type === 'zip');
  const comments = reports.filter((r) => r.type === 'comment');
  const mapItems = reports.filter((r) => r.type === 'spot' || r.type === 'sesh' || r.type === 'event');
  const listingReports = reports.filter((r) => r.type === 'listing');
  const profiles = reports.filter((r) => typeof r.reportedUserId === 'string');
  const pendingShops = shops.filter((s) => !s.reviewed);
  const pendingParks = parks.filter((p) => !p.reviewed);
  const usersById = useMemo(() => new Map(allUsers.map((u) => [u.id, u] as [string, User])), [allUsers]);

  // What a report is about: a short description, a thumbnail, and who made it.
  const describeReported = (report: ReportDoc): { label: string; title?: string; author?: PersonRef; media?: { url?: string; isVideo?: boolean }; gone?: boolean } | null => {
    const id = report.targetId;
    switch (report.type) {
      case 'post': {
        const post = posts.find((p) => p.id === id);
        if (!post) return { label: 'Post', gone: true };
        const first = post.mediaItems?.[0];
        const isVideo = first ? first.type === 'video' : post.mediaType === 'video';
        return { label: 'Post', title: post.caption, author: post.user, media: { url: first?.url || (isVideo ? post.videoUrlCompressed : post.imageUrl) || post.imageUrl, isVideo } };
      }
      case 'zip': {
        const zip = zips.find((z) => z.id === id);
        return zip ? { label: 'Zip', title: zip.caption, author: zip.user, media: { url: zip.videoUrl, isVideo: true } } : { label: 'Zip', gone: true };
      }
      case 'spot': {
        const spot = spots.find((sp) => sp.id === id);
        return spot ? { label: spot.category === 'skatepark' ? 'Skatepark' : 'Spot', title: [spot.name, spot.description].filter(Boolean).join(' - '), author: spot.addedBy, media: { url: spot.imageUrl || spot.photos?.[0]?.url } } : { label: 'Spot', gone: true };
      }
      case 'sesh': {
        const sesh = jamSeshes.find((j) => j.id === id);
        return sesh ? { label: 'Jam sesh', title: sesh.name, author: sesh.createdBy } : { label: 'Jam sesh', gone: true };
      }
      case 'event': {
        const event = events.find((e) => e.id === id);
        return event ? { label: 'Event', title: event.name, author: event.createdBy } : { label: 'Event', gone: true };
      }
      case 'listing': {
        const listing = listings.find((l) => l.id === id);
        return listing ? { label: 'Listing', title: listing.title, author: listing.seller, media: { url: listing.photos?.[0] } } : { label: 'Listing', gone: true };
      }
      default:
        return null;
    }
  };

  const bannedUsers = allUsers.filter((u) => u.isBanned);

  // --- Analytics (computed from the users already loaded in the app) ---
  const analytics = useMemo(() => {
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const people = allUsers.filter((u) => !u.isBot);
    const seenMs = (u: User) => {
      const v: any = u.lastActive;
      return v?.toMillis?.() ?? (typeof v === 'number' ? v : 0);
    };
    const active = (windowMs: number) => people.filter((u) => now - seenMs(u) <= windowMs).length;
    const joined = (windowMs: number) => people.filter((u) => typeof u.joinedAt === 'number' && now - u.joinedAt <= windowMs).length;
    const sportCounts = new Map<string, number>();
    people.forEach((u) => (u.sports || []).forEach((sp) => sportCounts.set(sp.name || sp.id, (sportCounts.get(sp.name || sp.id) || 0) + 1)));
    const topSports = [...sportCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    return {
      total: people.length,
      online: active(5 * 60 * 1000),
      day: active(DAY),
      week: active(7 * DAY),
      month: active(30 * DAY),
      newWeek: joined(7 * DAY),
      newMonth: joined(30 * DAY),
      tracked: people.filter((u) => typeof u.joinedAt === 'number').length,
      og: people.filter((u) => u.isOG).length,
      withCover: people.filter((u) => u.coverPhotoUrl).length,
      topSports,
    };
  }, [allUsers]);

  const reportCategories: { key: Category; count: number; list: ReportDoc[] }[] = [
    { key: 'content', count: postsAndZips.length, list: postsAndZips },
    { key: 'comments', count: comments.length, list: comments },
    { key: 'map', count: mapItems.length, list: mapItems },
    { key: 'listings', count: listingReports.length, list: listingReports },
    { key: 'profiles', count: profiles.length, list: profiles },
  ];

  const pendingCorrections = corrections.filter((c) => !c.reviewed);

  const submissionCategories: { key: Category; count: number }[] = [
    { key: 'shopSubmits', count: pendingShops.length },
    { key: 'parkSubmits', count: pendingParks.length },
    { key: 'corrections', count: pendingCorrections.length },
  ];

  const handleResolveCorrection = async (id: string) => {
    try {
      await updateDoc(doc(db, 'corrections', id), { reviewed: true });
      setCorrections((prev) => prev.map((c) => (c.id === id ? { ...c, reviewed: true } : c)));
    } catch (err) {
      console.error('Failed to resolve correction', err);
      alert('Could not mark this correction as reviewed.');
    }
  };

  const handleResolveDelete = (report: ReportDoc) => {
    if (report.type === 'post' && report.targetId) onDeletePost(report.targetId);
    else if (report.type === 'zip' && report.targetId) onDeleteZip(report.targetId);
    else if (report.type === 'comment' && report.targetId && report.parentType && report.parentId) {
      onDeleteComment(report.parentType, report.parentId, report.targetId);
    } else if (report.type === 'spot' && report.targetId) onDeleteSpot(report.targetId);
    else if (report.type === 'sesh' && report.targetId) onDeleteJamSesh(report.targetId);
    else if (report.type === 'event' && report.targetId) onDeleteEvent(report.targetId);
    else if (report.type === 'listing' && report.targetId) onDeleteListing(report.targetId);
    markResolved(report.id);
  };

  const handleResolveBan = (report: ReportDoc) => {
    if (report.reportedUserId) onBanUser(report.reportedUserId);
    markResolved(report.id);
  };

  const isSubmissionCategory = activeCategory === 'shopSubmits' || activeCategory === 'parkSubmits';
  const isCorrectionsCategory = activeCategory === 'corrections';
  const isBannedUsersCategory = activeCategory === 'bannedUsers';
  const activeList = !isSubmissionCategory && !isBannedUsersCategory && !isCorrectionsCategory ? reportCategories.find((c) => c.key === activeCategory)?.list ?? [] : [];
  const activeSubmissions: (Shop | Spot)[] = activeCategory === 'shopSubmits' ? pendingShops : activeCategory === 'parkSubmits' ? pendingParks : [];

  const handleAcceptSubmission = (id: string) => {
    if (activeCategory === 'shopSubmits') onAcceptShopSubmission(id);
    else if (activeCategory === 'parkSubmits') onAcceptParkSubmission(id);
    setExpandedSubmissionId(null);
  };

  return (
    <div className="fixed inset-0 z-[1400] flex flex-col bg-app-backdrop text-white">
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-red-500/30 bg-black/85 px-4 pb-3 backdrop-blur-md"
        style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}
      >
        <button
          onClick={() => {
            if (activeCategory) {
              setActiveCategory(null);
              setExpandedSubmissionId(null);
            } else {
              onClose();
            }
          }}
          className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/10"
          aria-label="Back"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <ExclamationIcon className="h-5 w-5 text-red-500" />
          <h1 className="text-lg font-black">
            {activeCategory ? CATEGORY_LABELS[activeCategory] : 'Developer'}
          </h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-6">
        {isLoading && <p className="text-center text-sm text-gray-400">Loading reports...</p>}
        {loadError && <p className="text-center text-sm text-red-400">{loadError}</p>}

        {!isLoading && !loadError && !activeCategory && (
          <div className="mx-auto max-w-md space-y-5">
            <section aria-label="Analytics" className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
              <p className="mb-3 px-1 text-xs font-bold uppercase tracking-[0.08em] text-gray-400">Analytics</p>
              <div className="rounded-2xl bg-black/50 p-4 text-center ring-1 ring-white/10">
                <p className="text-5xl font-black leading-none text-lime-400">{analytics.total.toLocaleString()}</p>
                <p className="mt-2 text-xs font-bold uppercase tracking-[0.08em] text-gray-400">Total users on RIDAZ</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2.5">
                {[
                  ['Online now', analytics.online],
                  ['Active today', analytics.day],
                  ['Active 7 days', analytics.week],
                  ['Active 30 days', analytics.month],
                  ['New · 7 days', analytics.tracked ? analytics.newWeek : '—'],
                  ['New · 30 days', analytics.tracked ? analytics.newMonth : '—'],
                ].map(([label, value]) => (
                  <div key={label as string} className="rounded-2xl bg-black/40 px-3 py-3 ring-1 ring-white/10">
                    <p className="text-2xl font-black text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                    <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
              {analytics.tracked === 0 && <p className="mt-2 px-1 text-[11px] text-gray-500">New-signup numbers start counting from the next signup.</p>}
              {contentCounts && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {([
                    ['Posts', contentCounts.posts], ['Zips', contentCounts.zips], ['Spots', contentCounts.spots],
                    ['Parks', contentCounts.parks], ['Shops', contentCounts.shops], ['Seshes', contentCounts.seshes],
                    ['Events', contentCounts.events], ['Listings', contentCounts.listings], ['Chats', contentCounts.threads],
                  ] as [string, number][]).map(([label, value]) => (
                    <div key={label} className="rounded-xl bg-black/40 px-2 py-2.5 text-center ring-1 ring-white/10">
                      <p className="text-lg font-black text-white">{value.toLocaleString()}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                    </div>
                  ))}
                </div>
              )}
              {analytics.topSports.length > 0 && (
                <div className="mt-3 rounded-2xl bg-black/40 p-3 ring-1 ring-white/10">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Top sports (riders)</p>
                  <ul className="space-y-1.5">
                    {analytics.topSports.map(([name, count]) => (
                      <li key={name} className="flex items-center gap-2 text-sm">
                        <span className="w-24 shrink-0 truncate font-semibold text-gray-200">{name}</span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-white/10"><span className="block h-full rounded-full bg-lime-400" style={{ width: `${Math.max(6, (count / analytics.topSports[0][1]) * 100)}%` }} /></span>
                        <span className="w-8 shrink-0 text-right text-xs font-bold text-gray-400">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 gap-3">
              {reportCategories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className="flex items-center justify-between rounded-2xl border border-red-500/30 bg-red-600/10 px-5 py-4 text-left transition-colors hover:bg-red-600/20"
                >
                  <span className="font-bold text-white">{CATEGORY_LABELS[cat.key]}</span>
                  <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-red-600 px-2 text-sm font-black text-white">
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>

            <div>
              <p className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.08em] text-gray-500">Submissions</p>
              <div className="grid grid-cols-1 gap-3">
                {submissionCategories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className="flex items-center justify-between rounded-2xl border border-lime-500/30 bg-lime-600/10 px-5 py-4 text-left transition-colors hover:bg-lime-600/20"
                  >
                    <span className="font-bold text-white">{CATEGORY_LABELS[cat.key]}</span>
                    <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-lime-500 px-2 text-sm font-black text-black">
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.08em] text-gray-500">Users</p>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => setActiveCategory('bannedUsers')}
                  className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-left transition-colors hover:bg-white/10"
                >
                  <span className="font-bold text-white">{CATEGORY_LABELS.bannedUsers}</span>
                  <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-white/20 px-2 text-sm font-black text-white">
                    {bannedUsers.length}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !loadError && activeCategory && !isSubmissionCategory && !isBannedUsersCategory && !isCorrectionsCategory && (
          <div className="mx-auto max-w-md space-y-3">
            {activeList.length === 0 && (
              <p className="text-center text-sm text-gray-400">No reports in this category.</p>
            )}
            {activeList.map((report) => {
              const isResolved = resolvedIds.has(report.id);
              const isProfileReport = activeCategory === 'profiles';
              return (
                <div
                  key={report.id}
                  className={`rounded-2xl border p-4 ${isResolved ? 'border-white/5 bg-white/[0.02] opacity-50' : 'border-white/10 bg-white/5'}`}
                >
                  <p className="text-xs uppercase tracking-[0.14em] text-gray-500">{formatTimestamp(report.timestamp)}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reported by</span>
                    <PersonChip person={{ id: report.reporterId }} usersById={usersById} onOpen={onViewUser} />
                  </div>
                  {isProfileReport ? (
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reported user</span>
                      <PersonChip person={{ id: report.reportedUserId }} usersById={usersById} onOpen={onViewUser} />
                    </div>
                  ) : report.type === 'general' ? (
                    <p className="mt-2.5 whitespace-pre-wrap break-words text-sm text-gray-200">{report.description || 'No description.'}</p>
                  ) : report.type === 'comment' ? (
                    <div className="mt-2.5 rounded-xl bg-black/30 p-3">
                      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Reported comment</p>
                      <ReportedComment report={report} usersById={usersById} onOpen={onViewUser} />
                    </div>
                  ) : (() => {
                    const item = describeReported(report);
                    if (!item) return <p className="mt-2.5 text-sm text-gray-200">Type: <span className="font-semibold text-white">{report.type}</span></p>;
                    return (
                      <div className="mt-2.5 rounded-xl bg-black/30 p-3">
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Reported {item.label.toLowerCase()}</p>
                        {item.gone ? (
                          <p className="text-sm text-gray-400">This {item.label.toLowerCase()} no longer exists (it may already be deleted).</p>
                        ) : (
                          <div className="flex gap-3">
                            <MediaThumb url={item.media?.url} isVideo={item.media?.isVideo} />
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <PersonChip person={item.author} usersById={usersById} onOpen={onViewUser} label="by" />
                              {item.title && <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-gray-200">{item.title}</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {!isResolved && (
                    <div className="mt-3 flex gap-2">
                      {isProfileReport ? (
                        <button
                          onClick={() => handleResolveBan(report)}
                          className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-red-500"
                        >
                          Ban user
                        </button>
                      ) : (
                        <button
                          onClick={() => handleResolveDelete(report)}
                          className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-red-500"
                        >
                          Delete content
                        </button>
                      )}
                      <button
                        onClick={() => markResolved(report.id)}
                        className="rounded-lg border border-white/15 px-3 py-2 text-xs font-bold text-gray-300 transition-colors hover:bg-white/10"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  {isResolved && <p className="mt-3 text-xs font-semibold text-lime-400">Resolved</p>}
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && !loadError && isCorrectionsCategory && (
          <div className="mx-auto max-w-md space-y-3">
            {pendingCorrections.length === 0 && (
              <p className="text-center text-sm text-gray-400">No pending corrections.</p>
            )}
            {pendingCorrections.map((c) => (
              <div key={c.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs uppercase tracking-[0.14em] text-gray-500">{formatTimestamp(c.createdAt)}</p>
                <p className="mt-1 text-sm font-bold text-white">
                  {c.targetName || c.targetId} <span className="ml-1 text-xs font-semibold uppercase text-lime-400">{c.targetType}</span>
                </p>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-200">{c.text}</p>
                <p className="mt-2 text-xs text-gray-500">From {'@' + String(c.submittedBy?.username || c.submittedBy?.id).toUpperCase()}</p>
                <p className="text-xs text-gray-600">ID: <span className="font-mono">{c.targetId}</span></p>
                <button
                  type="button"
                  onClick={() => handleResolveCorrection(c.id)}
                  className="mt-3 rounded-lg bg-lime-500 px-3 py-2 text-xs font-bold uppercase tracking-[0.06em] text-black transition-colors hover:bg-lime-400"
                >
                  Mark reviewed
                </button>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !loadError && isSubmissionCategory && (
          <div className="mx-auto max-w-md space-y-3">
            {activeSubmissions.length === 0 && (
              <p className="text-center text-sm text-gray-400">No pending submissions.</p>
            )}
            {activeSubmissions.map((item) => {
              const isExpanded = expandedSubmissionId === item.id;
              const photos = item.photos || [];
              return (
                <div key={item.id} className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                  <button
                    type="button"
                    onClick={() => setExpandedSubmissionId(isExpanded ? null : item.id)}
                    className="flex w-full items-center gap-3 p-4 text-left"
                  >
                    {photos[0] ? (
                      <img src={photos[0].url} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-black/40 text-gray-500">
                        <CameraIcon className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-white">{item.name}</p>
                      <p className="truncate text-xs text-gray-400">
                        by {'@' + String(item.addedBy.username).toUpperCase()} · {formatTimestamp(item.createdAt)}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">
                      {photos.length} photo{photos.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="space-y-3 border-t border-white/10 p-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.06em] text-gray-400">Title</p>
                        <p className="text-sm text-white">{item.name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.06em] text-gray-400">Bio</p>
                        <p className="text-sm leading-relaxed text-gray-200">{item.description}</p>
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.06em] text-gray-400">
                          Photos ({photos.length})
                        </p>
                        {photos.length > 0 ? (
                          <div className="grid grid-cols-3 gap-1.5">
                            {photos.map((p) => (
                              <img key={p.id} src={p.url} alt="" className="aspect-square w-full rounded-lg object-cover" />
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No photos submitted.</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAcceptSubmission(item.id)}
                        className="w-full rounded-lg bg-lime-500 px-3 py-2.5 text-xs font-bold uppercase tracking-[0.06em] text-black transition-colors hover:bg-lime-400"
                      >
                        Accept
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && !loadError && isBannedUsersCategory && (
          <div className="mx-auto max-w-md space-y-3">
            {bannedUsers.length === 0 && (
              <p className="text-center text-sm text-gray-400">No banned users.</p>
            )}
            {bannedUsers.map((user) => (
              <div key={user.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                <img src={user.avatarUrl || '/logo.png'} alt={user.username} className="h-11 w-11 shrink-0 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{'@' + String(user.username).toUpperCase()}</p>
                  <p className="text-xs text-gray-500">Banned {formatTimestamp(user.bannedAt) || 'previously'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onUnbanUser(user.id)}
                  className="shrink-0 rounded-lg bg-lime-500 px-3 py-2 text-xs font-bold text-black transition-colors hover:bg-lime-400"
                >
                  Unban
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperDashboard;
